import { useState } from 'react';

import Button from 'components/common/Button';
import { Drawer } from 'components/common/Drawer';
import CaretRightIcon from 'components/common/IconsV2/CaretRightIcon';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { useFacetsContext } from 'components/search/Facets/FacetsProvider';
import { QuickFilterDrawerBody } from 'components/search/QuickFilterPills/QuickFilterDrawerBody';
import { cn } from 'helpers/classnames';
import { pluralize, yieldToMain } from 'helpers/index';
import useMartyContext from 'hooks/useMartyContext';
import { usePrefersReducedMotion } from 'hooks/usePrefersReducedMotion';
import { useQuickFilterSectionExpansion } from 'hooks/useQuickFilterSectionExpansion';
import type { NavigationItem } from 'types/search';

export interface QuickFilterPillProps {
  /** Visible pill trigger text. */
  label: string;
  /** Title for the scoped drawer. */
  drawerTitle: string;
  /** Number of selected values for this pill's facet(s); the `(N)` suffix is hidden when 0. */
  selectedCount: number;
  totalProductCount: number; // Latest Total Result count based on the filters selection.
  dataTestId: string;
  sections: NavigationItem[];
  /** Active Drawer section resolved for ZC behavior; omitted for non-ZC pills. */
  activeSectionId?: string;
  /** Size section defaults; when a response changes them, the previous defaults close and the new ones open. */
  defaultExpandedSectionIds?: readonly string[];
}

/**
 * Renders a quick-filter pill as the trigger for its own scoped drawer,
 * reusing the Drawer structure and Show Results footer from FacetsDrawer.
 */
const QuickFilterPill = ({
  label,
  drawerTitle,
  selectedCount,
  totalProductCount,
  dataTestId,
  sections,
  activeSectionId,
  defaultExpandedSectionIds
}: QuickFilterPillProps) => {
  const { testId } = useMartyContext();
  const {
    state: { isLoading }
  } = useFacetsContext();
  const [isOpen, setIsOpen] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const { expandedSections, handleExpandedSectionsChange } = useQuickFilterSectionExpansion({
    activeSectionId,
    defaultExpandedSectionIds,
    isLoading,
    sections
  });
  const prefersReducedMotion = usePrefersReducedMotion();
  const resultCountLabel = `${totalProductCount} ${pluralize('Item', totalProductCount)}`;

  const handleOpenChange = async (open: boolean) => {
    await yieldToMain();
    setShouldAnimate(true);
    setIsOpen(open);
  };

  return (
    <Drawer.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Drawer.Trigger
        aria-label={`${drawerTitle}, ${selectedCount} selected`}
        data-test-id={testId(dataTestId)}
        className="body-s-light flex h-10 shrink-0 items-center gap-1 whitespace-nowrap rounded-[2.5px] border border-gray-300 bg-gray-0 px-3 py-[5px]"
      >
        {`${label}${selectedCount > 0 ? ` (${selectedCount})` : ''}`}
        <CaretRightIcon className="rotate-90" size={24} />
      </Drawer.Trigger>

      <Drawer.Content
        shouldAnimate={shouldAnimate && !prefersReducedMotion}
        className={cn('max-h-[98dvh]', { 'pointer-events-none': isLoading })}
        data-test-id={testId(`${dataTestId}-drawer`)}
      >
        <Drawer.Description>Filter search results by {drawerTitle}</Drawer.Description>
        <Drawer.Header>{drawerTitle}</Drawer.Header>

        <div
          className={cn('grow overflow-y-auto px-5 py-4 transition-opacity', { 'opacity-50': isLoading })}
          data-test-id={testId(`${dataTestId}-body`)}
        >
          <QuickFilterDrawerBody sections={sections} expandedSections={expandedSections} onExpandedSectionsChange={handleExpandedSectionsChange} />
        </div>

        <Drawer.Footer className="flex flex-col items-center gap-2">
          <Drawer.Announcement>{resultCountLabel}</Drawer.Announcement>
          <Drawer.Close asChild>
            <Button className="w-full [&_span]:items-baseline [&_span]:gap-1" data-test-id={testId('showResults')} height="small">
              Show results - <span className="text-xs zappos:font-normal zappos:normal-case">{resultCountLabel}</span>
            </Button>
          </Drawer.Close>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer.Root>
  );
};

// A render failure, including in the Drawer body, hides only this pill; the other pills stay usable.
export default withErrorBoundary('QuickFilterPill', QuickFilterPill);
